import type { Rule } from "antd/es/form";

import type {
  EarlyPayoutActiveTab,
  EarlyPayoutRuleType,
  EarlyPayoutSelectOption,
} from "./earlyPayoutTypes";

export const EARLY_PAYOUT_ADVANTAGE_INPUT_PROPS = {
  controls: false,
  precision: 0,
  step: 1,
} as const;

export const EARLY_PAYOUT_ADVANTAGE_RULES: Rule[] = [
  { required: true, message: "Advantage is required" },
  {
    validator: async (_, value) => {
      if (value === undefined || value === null || value === "") {
        return;
      }
      const parsed = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
        throw new Error("Advantage must be a whole number");
      }
    },
  },
];

export const EARLY_PAYOUT_DOWNLOAD_FILENAME =
  "sportsbook-early-payout-rulesets.csv";

export const EARLY_PAYOUT_SELECTORS_DOWNLOAD_FILENAME =
  "sportsbook-early-payout-selectors.csv";

export const EARLY_PAYOUT_TABS: Array<{
  label: string;
  value: EarlyPayoutActiveTab;
}> = [
  { label: "Rulesets", value: "rulesets" },
  { label: "Selectors", value: "selectors" },
];

export const SELECTOR_OPTIONS: EarlyPayoutSelectOption[] = [
  { label: "Sport", value: "sport" },
  { label: "Tournament", value: "tournament" },
  { label: "Event", value: "event" },
];

export const EARLY_PAYOUT_RULE_TYPE_OPTIONS: EarlyPayoutSelectOption<EarlyPayoutRuleType>[] =
  [
    { label: "WINNER", value: "WINNER" },
    { label: "TOTAL", value: "TOTAL" },
    { label: "TO_QUALIFY", value: "TO_QUALIFY" },
  ];

export const EARLY_PAYOUT_TABLE_LABELS = {
  ruleName: "Rule Name",
  ruleType: "Rule Type",
  sport: "Sport",
  market: "Market",
  advantage: "Advantage",
  selector: "Selector",
  id: "ID",
  status: "Status",
  rule: "Rule",
  actions: "Actions",
} as const;

export const EARLY_PAYOUT_FORM_LABELS = {
  ruleName: "Rule Name",
  ruleType: "Rule Type",
  sport: "Sport",
  market: "Market",
  advantage: "Advantage",
  conditions: "Conditions",
  lines: "Lines",
  timeStart: "Time Start",
  timeEnd: "Time End",
  selector: "Selector",
  selectorType: "Selector Type",
  rule: "Rule",
  tournament: "Tournament",
  event: "Event",
  details: "Details",
  status: "Status",
} as const;

export const EARLY_PAYOUT_PLACEHOLDERS = {
  ruleName: "Rule Name",
  ruleType: "Select...",
  sport: "Select...",
  market: "Select...",
  advantage: "Advantage",
  soccerTotalTime: "00:00",
  selector: "Select...",
  rule: "Select...",
  tournament: "Select...",
  event: "Select...",
  details: "312 - Sport: Baseball / Category: USA / Tournament: MLB",
  status: "Status",
} as const;

export const SOCCER_TOTAL_MATCH_MAX_MINUTES = 90;

export const EARLY_PAYOUT_SOCCER_TOTAL_TIME_VALIDATION_MESSAGE =
  "Time must use MM:SS match time from 00:00 through 90:00";

export const EARLY_PAYOUT_SOCCER_TOTAL_TIME_REQUIRED_MESSAGE =
  "Start and end time are required";

export const EARLY_PAYOUT_SOCCER_TOTAL_TIME_RANGE_VALIDATION_MESSAGE =
  "End time must be later than start time";

export const EARLY_PAYOUT_SOCCER_TOTAL_TIME_OVERLAP_VALIDATION_MESSAGE =
  "Time ranges cannot overlap across lines";

export const EARLY_PAYOUT_MODAL_TITLES = {
  createRuleset: "Create Early Payout Ruleset",
  editRuleset: "Edit Early Payout Ruleset",
  createSelector: "Create Early Payout Selector",
  viewSelectorRule: "Early Payout Rule",
} as const;

export const EARLY_PAYOUT_ACTION_LABELS = {
  createRuleset: "Create Ruleset",
  updateRuleset: "Update Ruleset",
  createSelector: "Create Selector",
  addSoccerTotalLine: "Add Line",
  removeSoccerTotalLine: "Remove Line",
  download: "Download early payout report",
  close: "Close",
} as const;

export const EARLY_PAYOUT_CONFIRM_MESSAGES = {
  deleteRulesetTitle: "Delete Early Payout Ruleset",
  deleteRulesetContent:
    "This ruleset will be removed, along with all selectors that use the same rule.",
  deleteSelectorTitle: "Delete Early Payout Selector",
  deleteSelectorContent: "This selector will be removed from the table.",
  okText: "Delete",
  cancelText: "Cancel",
} as const;

export const EARLY_PAYOUT_ERROR_MESSAGES = {
  exportRulesets:
    "Could not export early payout rulesets. Try again or contact support.",
  exportSelectors:
    "Could not export early payout selectors. Try again or contact support.",
  exportReporting:
    "Could not export early payout reporting. Try again or contact support.",
  loadRulesets:
    "Could not load early payout rulesets. Try again or contact support.",
  loadSportsDropdown:
    "Could not load sports. Try again or contact support.",
  loadMarketsDropdown:
    "Could not load markets. Try again or contact support.",
  loadSelectors:
    "Could not load early payout selectors. Try again or contact support.",
  loadLeaguesDropdown:
    "Could not load tournaments. Try again or contact support.",
  loadEventsDropdown:
    "Could not load events. Try again or contact support.",
} as const;

export const SOCCER_TOTAL_LINE_STEP = 0.25;
export const SOCCER_TOTAL_LINE_MIN = 1;
export const SOCCER_TOTAL_DEFAULT_LINE_VALUE = 1.5;

export const SOCCER_TOTAL_DEFAULT_CONDITIONS = [
  {
    lineValue: SOCCER_TOTAL_DEFAULT_LINE_VALUE,
    timeStart: "",
    timeEnd: "",
  },
] as const;

export const EARLY_PAYOUT_TEST_IDS = {
  rulesetsTable: "early-payout-rulesets-table",
  selectorsTable: "early-payout-selectors-table",
  reportingTable: "early-payout-reporting-table",
  selectorRuleModal: "early-payout-selector-rule-modal",
  conditionsSection: "early-payout-conditions-section",
  soccerTotalConditions: "early-payout-soccer-total-conditions",
  soccerTotalConditionsView: "early-payout-soccer-total-conditions-view",
  moneylineConditions: "early-payout-moneyline-conditions",
} as const;
